import type { ReactElement } from 'react';
import { Badge } from '@/components/ui/badge';
import type { AlertSecurityGroup } from '@kirin/api-client/contracts/alerts';
import {
  ORGANIZATION_SCOPE_LABEL,
  securityGroupLabel,
} from './security-groups';

/**
 * "Security Group" key:value row inside an expanded alert (KIR-3800, AC #13/#14).
 *
 * One group names it outright. Several means the alert deduplicated occurrences
 * across users in different groups — it says how many and points at Show
 * Occurrences, which breaks the group down per occurrence.
 *
 * `securityGroups` is supplied by the API and is `undefined` when the feature
 * flag is off (or on the personal dashboard, whose alert rows carry no group
 * attribution), in which case this renders nothing.
 */
export function AlertSecurityGroupDetail({
  securityGroups,
}: {
  securityGroups: AlertSecurityGroup[] | undefined;
}): ReactElement | null {
  if (securityGroups === undefined) return null;

  // Requested, but nobody behind the alert is grouped — they run on the
  // organization's own policies. The API normally spells this out with an
  // explicit Organization entry, so this branch is defensive.
  const chips =
    securityGroups.length === 0
      ? [{ key: ORGANIZATION_SCOPE_LABEL, label: ORGANIZATION_SCOPE_LABEL }]
      : securityGroups.map(group => ({
          // Keyed by id, never by name: a soft-deleted group and a live one can
          // share a name (the unique index only covers live rows), and a
          // duplicate React key would drop one of the two chips.
          key: group.id,
          label: securityGroupLabel(group),
        }));

  return (
    // Label and value sit on ONE line ("Security Group: Engineering") rather
    // than stacking. `flex-wrap` only kicks in for a multi-group alert whose
    // chips genuinely overflow — the common single-group case always reads as
    // one row.
    <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Security Group:
      </p>
      {chips.map(chip => (
        <Badge key={chip.key} variant="outline">
          {chip.label}
        </Badge>
      ))}
      {chips.length > 1 ? (
        <span className="text-xs text-muted-foreground">
          {chips.length} groups — see Show Occurrences for the group of each
          user
        </span>
      ) : null}
    </div>
  );
}

AlertSecurityGroupDetail.displayName = 'AlertSecurityGroupDetail';
